import { Head, Link } from "@inertiajs/react"
import Markdown from "react-markdown"

import { Button } from "@/components/ui/button"

interface LegalLayoutProps {
  title: string
  content: string
}

export default function LegalLayout({ title, content }: LegalLayoutProps) {
  return (
    <>
      <Head title={title} />

      <div className="bg-background flex min-h-svh flex-col">
        {/* Header */}
        <header className="border-border bg-background/80 sticky top-0 z-50 flex items-center justify-between border-b px-6 py-4 backdrop-blur-md">
          <Link href="/" className="flex items-center">
            <img
              src="/images/krown_logo_header.png"
              alt="KrownFT"
              className="h-8"
            />
          </Link>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              className="text-white hover:bg-white/5"
              asChild
            >
              <Link href="/sign_in">Iniciar sesión</Link>
            </Button>
            <Button variant="accent" asChild>
              <Link href="/sign_up">Registrarse</Link>
            </Button>
          </div>
        </header>

        {/* Content */}
        <main className="mx-auto w-full max-w-4xl px-6 py-12 md:py-16">
          <Markdown
            components={{
              h1: ({ children }) => (
                <h1 className="mb-8 text-3xl font-bold text-white md:text-4xl">
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 className="mt-10 mb-4 text-xl font-bold text-white">
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="mt-6 mb-3 text-lg font-semibold text-white">
                  {children}
                </h3>
              ),
              p: ({ children }) => (
                <p className="text-krown-grey-30 mb-4 text-sm leading-relaxed md:text-base">
                  {children}
                </p>
              ),
              ul: ({ children }) => (
                <ul className="text-krown-grey-30 mb-4 list-disc space-y-2 pl-6 text-sm leading-relaxed md:text-base">
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="text-krown-grey-30 mb-4 list-decimal space-y-2 pl-6 text-sm leading-relaxed md:text-base">
                  {children}
                </ol>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold text-white">{children}</strong>
              ),
              a: ({ href, children }) => {
                if (href?.startsWith("/")) {
                  return (
                    <Link
                      href={href}
                      className="text-krown-100 hover:underline"
                    >
                      {children}
                    </Link>
                  )
                }
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-krown-100 hover:underline"
                  >
                    {children}
                  </a>
                )
              },
            }}
          >
            {content}
          </Markdown>
        </main>

        {/* Footer */}
        <footer className="border-border mt-auto border-t px-6 py-10">
          <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 md:flex-row md:justify-between">
            <img
              src="/images/krown_logo_header.png"
              alt="KrownFT"
              className="h-6 opacity-60"
            />
            <div className="text-muted-foreground flex flex-wrap justify-center gap-6 text-sm">
              <Link
                href="/terminos"
                className="transition-colors hover:text-white"
              >
                Términos y Condiciones
              </Link>
              <Link
                href="/privacidad"
                className="transition-colors hover:text-white"
              >
                Política de Privacidad
              </Link>
              <Link
                href="/cookies"
                className="transition-colors hover:text-white"
              >
                Política de Cookies
              </Link>
              <Link
                href="/ley-california"
                className="transition-colors hover:text-white"
              >
                Ley de California
              </Link>
              <Link
                href="/cerrar-cuenta"
                className="transition-colors hover:text-white"
              >
                Cerrar Cuenta
              </Link>
            </div>
            <p className="text-krown-grey-50 text-xs">
              &copy; {new Date().getFullYear()} KrownFT. Todos los derechos
              reservados.
            </p>
          </div>
        </footer>
      </div>
    </>
  )
}
